import type { useTranslations } from 'next-intl';
import { Icon } from '@/components/ui/icon';
import { DietaryIndicators, type DietaryItem } from '@/components/ui/dietary-indicators';
import { VariantChip } from '@/components/ui/variant-chip';
import { RecipeCardLink } from './recipe-card-link';
import { RecipeCardActions } from './recipe-card-actions';
import { RecipeImage } from './recipe-image';
import styles from './recipe-card.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

type VariantKey = 'quick' | 'classic' | 'bougie';

/**
 * Localized label for the status pill floated over the card media. Published
 * recipes show no pill (the common case needs no signal). Uses the root
 * translator's `cookbook.status*` keys — the same catalogue the cookbook
 * list/table view uses — so the grid and list agree instead of the grid
 * leaking the raw DB enum ("pending_review") to non-English viewers.
 */
function statusPillLabel(
  status: string,
  t: ReturnType<typeof useTranslations>,
): string | null {
  switch (status) {
    case 'draft':
      return t('cookbook.statusDraft');
    case 'pending_review':
      return t('cookbook.statusPendingReview');
    case 'archived':
      return t('cookbook.statusArchived');
    default:
      return null;
  }
}

interface RecipeCardProps {
  /**
   * Root-namespace translator (no namespace argument — same shape as
   * `getTranslations()`/`useTranslations()` called with no args). Passed in
   * rather than fetched internally: this component renders from BOTH Server
   * Components AND a Client Component (NewRecipesStream). See #298.
   */
  t: ReturnType<typeof useTranslations>;
  recipeId: string;
  slug: string;
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  prepTimeMinutes?: number | null;
  cookTimeMinutes?: number | null;
  servings?: number | null;
  variantType?: string | null;
  /** When false, the card deep-links to `?variant={variantType}`. */
  variantIsDefault?: boolean;
  isVegan?: boolean;
  isVegetarian?: boolean;
  isGlutenFree?: boolean;
  isDairyFree?: boolean;
  /** Per-serving calories. */
  caloriesPerServing?: number | null;
  /** Editor's-pick / feature composition (larger, horizontal on desktop). */
  feature?: boolean;
  /** Optional eyebrow (e.g. "Editor's pick") shown above the title. */
  eyebrow?: string;
  status?: string;
  /**
   * When provided, a quiet remove control floats over the media (top-left) —
   * one click, no confirm. Used by the cookbook detail view to remove a recipe
   * from that cookbook. Only pass from a client component.
   */
  onRemove?: () => void;
  /** Accessible label for the remove control (required when onRemove is set). */
  removeLabel?: string;
  /** Hides the Save bookmark float (e.g. inside a cookbook where Save is moot). */
  showSave?: boolean;
  /**
   * "In N cookbooks" mode. When set (the all-saved view), the card body shows
   * `savedInLabel` under the title — a card MODE, not a page-local redraw.
   */
  savedIn?: number | null;
  /** Pre-rendered "in N cookbooks" label (caller owns the plural/i18n). */
  savedInLabel?: string;
}

/**
 * Canonical recipe card (#302 lean card). The card face carries only what
 * scans at a glance: image (with the Quick chip, calories, and a Save
 * bookmark floated over it), title, description, and a single quiet line of
 * time · dietary icons. Servings and the Hashimoto tier read on the recipe
 * detail hero, not the card; Plan (add to meal plan) also lives on the detail
 * page. The image is the morph source into that hero (shared `--r-hero`).
 */
export function RecipeCard({
  t,
  recipeId,
  slug,
  title,
  description,
  imageUrl,
  prepTimeMinutes,
  cookTimeMinutes,
  servings,
  variantType,
  variantIsDefault = true,
  isVegan,
  isVegetarian,
  isGlutenFree,
  isDairyFree,
  caloriesPerServing,
  feature = false,
  eyebrow,
  status,
  onRemove,
  removeLabel,
  showSave = true,
  savedIn,
  savedInLabel,
}: RecipeCardProps) {
  const prep = prepTimeMinutes ?? 0;
  const cook = cookTimeMinutes ?? 0;
  const totalTime = prep + cook;
  // Surface the prep/active split for longer recipes.
  const showSplitTime = totalTime > 30 && prep > 0 && cook > 0;

  const resolvedVariant: VariantKey =
    variantType === 'quick' || variantType === 'classic' || variantType === 'bougie'
      ? variantType
      : 'classic';

  const dietaryItems: DietaryItem[] = [];
  if (isVegan) dietaryItems.push({ key: 'vegan', label: t('dietary.vegan') });
  else if (isVegetarian) dietaryItems.push({ key: 'vegetarian', label: t('dietary.vegetarian') });
  if (isGlutenFree) dietaryItems.push({ key: 'glutenFree', label: t('dietary.glutenFree') });
  if (isDairyFree) dietaryItems.push({ key: 'dairyFree', label: t('dietary.dairyFree') });

  const href =
    variantType && !variantIsDefault
      ? `/recipes/${slug}?variant=${variantType}`
      : `/recipes/${slug}`;

  const statusText = status ? statusPillLabel(status, t) : null;

  return (
    <article className={cx(styles.card, feature && styles.feature)}>
      <RecipeCardLink
        href={href}
        slug={slug}
        cacheData={{
          title,
          imageUrl: imageUrl ?? null,
          description: description ?? null,
          prepTimeMinutes: prepTimeMinutes ?? 0,
          cookTimeMinutes: cookTimeMinutes ?? 0,
          servings: servings ?? 0,
          caloriesPerServing: caloriesPerServing ?? null,
          categoryName: null,
        }}
        className={styles.cardLink}
      >
        <div
          className={styles.media}
          style={{ viewTransitionName: `recipe-img-${slug}` }}
          data-morph-source={slug}
        >
          <RecipeImage
            recipeId={recipeId}
            variantType={variantType}
            initialImageUrl={imageUrl}
            alt={title}
            sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
            imageClassName={styles.image}
            compact
          />

          {/* Only Quick gets a chip — it's a speed signal. Classic (default)
              and Bougie read from the cook time + photo, no chip needed. The
              card owns the corner placement; VariantChip owns the pill itself. */}
          {resolvedVariant === 'quick' && (
            <span className={styles.variantpos}>
              <VariantChip variant={resolvedVariant} label={t('recipes.variants.quick')} />
            </span>
          )}

          {caloriesPerServing != null && (
            <span
              className={styles.kcal}
              aria-label={t('recipes.caloriesPerServing', {
                count: Math.round(caloriesPerServing),
              })}
            >
              <Icon name="flame" size={13} strokeWidth={1.75} aria-hidden="true" />
              {Math.round(caloriesPerServing)}
            </span>
          )}

          {statusText && (
            <span className={styles.status} data-status={status}>
              {statusText}
            </span>
          )}
        </div>

        <div className={styles.body}>
          {eyebrow ? <div className={styles.eyebrow}>{eyebrow}</div> : null}
          <h3 className={styles.title}>{title}</h3>
          {savedIn != null && savedInLabel ? (
            <p className={styles.savedIn}>
              <Icon name="book" size={13} className={styles.savedInIcon} />
              {savedInLabel}
            </p>
          ) : null}
          {description && <p className={styles.desc}>{description}</p>}

          {/* One quiet line: time on the left, the muted dietary cluster on the
              right, separated from the body by a hairline. Servings + Hashimoto
              live on the detail hero; Save floats over the image. */}
          {(totalTime > 0 || dietaryItems.length > 0) && (
            <div className={styles.foot}>
              {totalTime > 0 ? (
                <span
                  className={styles.time}
                  title={
                    showSplitTime ? t('recipes.minutesSplitTitle', { prep, cook }) : undefined
                  }
                >
                  <Icon name="timer" size={15} strokeWidth={1.75} className={styles.timeIcon} />
                  {showSplitTime
                    ? t('recipes.minutesSplitShort', { prep, cook })
                    : t('recipes.minutes', { count: totalTime })}
                </span>
              ) : (
                <span aria-hidden="true" />
              )}
              {dietaryItems.length > 0 && (
                <DietaryIndicators
                  items={dietaryItems}
                  density="card"
                  className={styles.dietwrap}
                  label={t('recipes.dietaryInformation')}
                />
              )}
            </div>
          )}
        </div>
      </RecipeCardLink>

      {/* Save bookmark — floated over the image, rendered as a sibling of the
          link so its click never triggers navigation. */}
      {showSave && <RecipeCardActions recipeId={recipeId} variantType={resolvedVariant} />}

      {/* Remove-from-cookbook — quiet corner control, one click, no confirm.
          Sibling of the link so its click never navigates. */}
      {onRemove && (
        <button
          type="button"
          className={styles.removeFloat}
          onClick={onRemove}
          aria-label={removeLabel}
          title={removeLabel}
        >
          <Icon name="x" size={16} strokeWidth={1.75} />
        </button>
      )}
    </article>
  );
}
